import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `compliance` of the console gateway — owned by plan-002 unit **RB-28** (design §15.6.8).
 *
 * Consent, retention, operator access and person-data requests (P14-05's screen), over the four
 * routes RB-28 added. The register is EMPTY: every operation is served.
 *
 * ── THE MAPPING ──────────────────────────────────────────────────────────────────────────
 * Every `…Text` and label is worded here from facts (design §15.5.2); the API sends none:
 *
 *   * **Consent lines** arrive one per medium, with a sample line and a language count. The
 *     surface label and the state label are the console's copy for that medium; a medium whose
 *     lines are not all on reads "Off".
 *   * **Retention** arrives for all six classes. The screen offers the four the prototype draws
 *     (recordings, transcripts, orders, caller details); survey answers and the audit log are not
 *     selectable there, exactly as the sandbox has it. Each option is labelled with
 *     `retainDayLabel`; `0` is "Delete with the call". A stored value outside the offer is passed
 *     through as `chosenRetainDay`, and the screen renders it as the fault it is.
 *   * **Operator access** is the account's switch. "Never" when no change is on record.
 *   * **A person-data request** carries `judgePersonDataRequest`'s verdict as milliseconds; the
 *     view wants whole hours, rounded UP so a request is never shown as due sooner, or late by
 *     less, than it is. The time a finished request took is rounded DOWN (`elapsedHourOf`).
 *
 * ── WHAT THE CALLER IS TOLD ABOUT QUEUED WORK (D3) ────────────────────────────────────────
 * The API records a person-data request and queues it, and says in `execution` whether a worker
 * handler is deployed to run it (in plan-002 it is not). The view has no field for that, so the
 * slice says it in `requestedText` — "… · queued; nothing processes it until the data worker is
 * deployed" — rather than let the row read as if the work were running. The retention sweep's
 * equivalent (`retention_sweep.handler_deployed`) has no place in the view at all; the console's
 * copy already says the sweep is WeTalk's, and the gap is recorded in RB-28's report.
 *
 * ── COLLECTING AN EXPORT (customer-portal review item 197, 2026-09-26) ────────────────────
 * A row's `download_url` is always `null`; `download_available_until` says whether a completed
 * export's file is still kept, and `downloadPersonDataExport` asks for a short-lived link to it —
 * audited per download, so only when the button is pressed. An API that predates the field sends
 * none, and the row then offers no download rather than failing the screen.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  ComplianceView,
  ConsentLineRowView,
  ConsentLineSavedView,
  RecordingInForceView,
  MediumView,
  OperatorAccessView,
  PersonDataExportLinkView,
  PersonDataRequestDraftView,
  PersonDataRequestKindView,
  PersonDataRequestProgressView,
  PersonDataRequestRowView,
  RetentionClassView,
  RetentionPreviewView,
  RetentionSelectView,
} from "../gateway.js";
import { retainDayLabel, retentionClassLabel } from "../governance-view.js";
import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readWholeNumber,
} from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    getCompliance(): Promise<ComplianceView>;
    /** The chosen value must be one the class offers; a free number has no sweep rule. */
    saveRetentionPolicy(retentionClass: string, retainDay: number): Promise<ComplianceView>;
    /** What the next sweep would remove with that period. Saves nothing (review item 203). */
    previewRetentionPolicy(retentionClass: string, retainDay: number): Promise<RetentionPreviewView>;
    setOperatorAccess(enabled: boolean): Promise<ComplianceView>;
    /** AC-34: an export or a delete, completing within 24 hours and tracked until it does. */
    requestPersonData(draft: PersonDataRequestDraftView): Promise<PersonDataRequestRowView>;
    /** A short-lived link to a completed export's file. Each call is audited (review item 197). */
    downloadPersonDataExport(personDataRequestId: string): Promise<PersonDataExportLinkView>;
    /** Review item 204: the workspace's own wording of the spoken notice, for one language. */
    saveConsentLine(languageCode: string, text: string): Promise<ConsentLineSavedView>;
    /** Review item 204: WeTalk's standard notice again, for one language. */
    restoreStandardConsentLine(languageCode: string): Promise<ConsentLineSavedView>;
  }
}

export const complianceGap: readonly GatewayGap[] = [];

const HOUR_MS = 3_600_000;

const MEDIUM: readonly MediumView[] = ["voice", "web_chat", "whatsapp", "viber"];

/** Where each medium's line is heard or seen. */
const SURFACE_LABEL: Readonly<Record<MediumView, string>> = validatedDisplayView({
  get voice() { return portalText("shell", "calls_0a19b7e2"); },
  get web_chat() { return portalText("shell", "web_chat_109d8596"); },
  whatsapp: "WhatsApp",
  get viber() { return portalText("shell", "viber_70774829"); },
});

/** The four the Compliance screen offers, in the prototype's order. */
const SHOWN_CLASS: readonly RetentionClassView[] = ["recording", "transcript", "order", "caller_detail"];

/** Every class the API names, for reading one back. */
const RETENTION_CLASS: readonly RetentionClassView[] = [...SHOWN_CLASS, "survey_result", "audit"];

const RETENTION_NOTE: Readonly<Record<RetentionClassView, string>> = validatedDisplayView({
  get recording() { return portalText("shell", "deleted_automatically_transcripts_can_outlive_the_audio_13389802"); },
  get transcript() { return portalText("shell", "used_for_analytics_the_review_inbox_and_version_comparison_6cc45a32"); },
  get "order"() { return portalText("shell", "exports_are_yours_to_keep_regardless_25c3dc4d"); },
  get survey_result() { return portalText("shell", "exports_are_yours_to_keep_regardless_25c3dc4d"); },
  get caller_detail() { return portalText("shell", "keeping_them_is_what_lets_the_agent_greet_a_regular_by_nam_7cf46ac4"); },
  get audit() { return portalText("shell", "fixed_at_24_months_this_one_is_not_yours_to_shorten_42ea3c94"); },
});

const KIND: readonly PersonDataRequestKindView[] = ["export", "delete"];

/** Whole integers, which `readWholeNumber` does not insist on (and `-1` is a legal retention). */
function readInteger(value: unknown, path: string): number {
  const found = readWholeNumber(value, path);
  if (!Number.isInteger(found)) throw new MalformedResponseError(path, "an integer", value);
  return found;
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function readMedium(value: unknown, path: string): MediumView {
  const raw = readText(value, path);
  const found = MEDIUM.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a medium", raw);
  return found;
}

function readKind(value: unknown, path: string): PersonDataRequestKindView {
  const raw = readText(value, path);
  const found = KIND.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "export or delete", raw);
  return found;
}

/** Review #66's `recording` block. Absent from an older API, which reads as unknown. */
function recordingOf(wire: unknown, path: string): RecordingInForceView | null {
  if (wire === undefined || wire === null) return null;
  const one = readRecord(wire, path);
  const names = (value: unknown, at: string): readonly string[] =>
    readArray(value, at).map((entry, index) => readText(entry, `${at}[${String(index)}]`));
  return {
    recordingVoiceAgent: names(one["recording_voice_agent"], `${path}.recording_voice_agent`),
    notRecordingVoiceAgent: names(one["not_recording_voice_agent"], `${path}.not_recording_voice_agent`),
    spokenNotice: readArray(one["spoken_notice"], `${path}.spoken_notice`).map((entry, index) => {
      const at = `${path}.spoken_notice[${index}]`;
      const notice = readRecord(entry, at);
      const text = readText(notice["text"], `${at}.text`);
      // Review #204's fields. An API that predates them only ever spoke the standard line.
      const source = notice["source"];
      if (source !== undefined && source !== "workspace" && source !== "standard") {
        throw new MalformedResponseError(`${at}.source`, "workspace or standard", source);
      }
      return validatedDisplayView({
        languageCode: readText(notice["language_code"], `${at}.language_code`),
        languageName: readText(notice["language_name"], `${at}.language_name`),
        text,
        source: source === undefined ? "standard" : source,
        get standardText() { return notice["standard_text"] === undefined ? text : readNullableText(notice["standard_text"], `${at}.standard_text`); },
        outOfDateVoiceAgent:
          notice["out_of_date_voice_agent"] === undefined
            ? []
            : names(notice["out_of_date_voice_agent"], `${at}.out_of_date_voice_agent`),
      });
    }),
  };
}

/** `ConsentLineSaved`: the Compliance body after the save, and the agents republished. */
function consentLineSavedOf(body: unknown, operationId: string, context: SliceContext): ConsentLineSavedView {
  const data = readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
  return {
    compliance: complianceViewOf({ data: data["compliance"] }, `${operationId}.compliance`, context),
    republishedVoiceAgent: readArray(data["republished_voice_agent"], `${operationId}.republished_voice_agent`).map(
      (one, index) => readText(one, `${operationId}.republished_voice_agent[${index}]`),
    ),
  };
}

function consentLineOf(wire: unknown, path: string): ConsentLineRowView {
  const row = readRecord(wire, path);
  const medium = readMedium(row["medium"], `${path}.medium`);
  const sample = readRecord(row["sample"], `${path}.sample`);
  const enabled = readBoolean(row["enabled"], `${path}.enabled`);
  return validatedDisplayView({
    consentLineId: readText(sample["consent_line_id"], `${path}.sample.consent_line_id`),
    medium,
    surfaceLabel: SURFACE_LABEL[medium],
    text: readText(sample["text"], `${path}.sample.text`),
    enabled,
    get stateLabel() { return !enabled ? portalText("shell", "off_e3de5ab0") : medium === "voice" ? portalText("shell", "on_e0049a66") : portalText("shell", "shown_in_the_widget_998a803d"); },
    languageCount: readInteger(row["language_count"], `${path}.language_count`),
  });
}

function retentionOf(wire: unknown, path: string): readonly RetentionSelectView[] {
  const byClass = new Map<string, Readonly<Record<string, unknown>>>();
  readArray(wire, path).forEach((one, index) => {
    const row = readRecord(one, `${path}[${String(index)}]`);
    byClass.set(readText(row["class"], `${path}[${index}].class`), row);
  });
  return SHOWN_CLASS.map((retentionClass) => {
    const at = `${path}.${retentionClass}`;
    const row = byClass.get(retentionClass);
    if (row === undefined) throw new MalformedResponseError(at, "a retention class row", undefined);
    return validatedDisplayView({
      class: retentionClass,
      get label() { return retentionClassLabel(retentionClass); },
      note: RETENTION_NOTE[retentionClass],
      option: readArray(row["option"], `${at}.option`).map((one, index) => {
        const retainDay = readInteger(one, `${at}.option[${index}]`);
        return validatedDisplayView({ retainDay, get label() { return retainDay === 0 ? portalText("shell", "delete_with_the_call_dab291f6") : retainDayLabel(retainDay); } });
      }),
      chosenRetainDay: readInteger(row["retain_day"], `${at}.retain_day`),
      defaultRetainDay: readInteger(row["policy_retain_day"], `${at}.policy_retain_day`),
    });
  });
}

function operatorAccessOf(wire: unknown, now: Date, context: SliceContext): OperatorAccessView {
  const row = readRecord(wire, "operator_access");
  const changedAt = readNullableText(row["changed_at"], "operator_access.changed_at");
  const playedAt = readNullableText(row["last_played_by_operator_at"], "operator_access.last_played_by_operator_at");
  return validatedDisplayView({
    enabled: readBoolean(row["enabled"], "operator_access.enabled"),
    get changedText() { return changedAt === null ? "never" : context.formatWhen(changedAt, now); },
    changedByName: readNullableText(row["changed_by_name"], "operator_access.changed_by_name"),
    get lastPlayedByOperatorText() { return playedAt === null ? null : context.formatWhen(playedAt, now); },
  });
}

/** Milliseconds as whole hours, rounded up (see the header). */
function hourOf(ms: number): number {
  return Math.ceil(ms / HOUR_MS);
}

/**
 * How long a finished request took, in whole hours rounded DOWN. Rounding up is for a deadline
 * still running; a request done in a second is not "done in 1 hour". `within_deadline` is the
 * API's own verdict, so rounding here never turns a late request into a punctual one.
 */
function elapsedHourOf(ms: number): number {
  return Math.floor(ms / HOUR_MS);
}

function progressOf(wire: unknown, path: string): PersonDataRequestProgressView {
  const row = readRecord(wire, path);
  const state = readText(row["state"], `${path}.state`);
  if (state === "pending") {
    return { state, remainingHour: hourOf(readInteger(row["remaining_ms"], `${path}.remaining_ms`)) };
  }
  if (state === "overdue") {
    return { state, overdueHour: hourOf(readInteger(row["overdue_ms"], `${path}.overdue_ms`)) };
  }
  if (state === "completed") {
    return {
      state,
      withinDeadline: readBoolean(row["within_deadline"], `${path}.within_deadline`),
      elapsedHour: elapsedHourOf(readInteger(row["elapsed_ms"], `${path}.elapsed_ms`)),
    };
  }
  throw new MalformedResponseError(`${path}.state`, "pending, overdue or completed", state);
}

/**
 * A completed export's file (review item 197). `download_available_until` absent — an API that
 * predates it — is read as no file, so the screen keeps working and offers no download.
 */
function exportFileOf(
  row: Readonly<Record<string, unknown>>,
  path: string,
  now: Date,
  context: SliceContext,
): PersonDataRequestRowView["exportFile"] {
  const wire = row["download_available_until"];
  if (wire === undefined || wire === null) return null;
  const until = readText(wire, `${path}.download_available_until`);
  const untilMs = Date.parse(until);
  if (Number.isNaN(untilMs)) throw new MalformedResponseError(`${path}.download_available_until`, "a date-time", until);
  if (untilMs <= now.getTime()) return { state: "removed" };
  return validatedDisplayView({ state: "kept", get untilText() { return context.formatWhen(until, now); } });
}

/** "in 15 minutes", for a link that stops working at `expiresAt`. */
function expiresInText(expiresAt: string, now: Date): string {
  const minute = Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (Number.isNaN(minute)) throw new MalformedResponseError("expires_at", "a date-time", expiresAt);
  if (minute <= 0) return "expired";
  return minute === 1 ? portalText("shell", "in_1_minute_2298b716") : portalText("shell", "in_value_minutes_45992b3b", { v0: String(String(minute)) });
}

function affectedCountOf(wire: unknown, path: string): Readonly<Record<string, number>> | null {
  if (wire === null) return null;
  const row = readRecord(wire, path);
  const counted: Record<string, number> = {};
  for (const [store, count] of Object.entries(row)) counted[store] = readInteger(count, `${path}.${store}`);
  return counted;
}

/** One `PersonDataRequestRow` as the panel renders it. */
export function personDataRequestRowOf(
  wire: unknown,
  path: string,
  now: Date,
  context: SliceContext,
): PersonDataRequestRowView {
  const row = readRecord(wire, path);
  const execution = readRecord(row["execution"], `${path}.execution`);
  const queued = readText(execution["state"], `${path}.execution.state`) === "queued";
  const handlerDeployed = readBoolean(execution["handler_deployed"], `${path}.execution.handler_deployed`);
  if (row["download_url"] !== null) {
    throw new MalformedResponseError(
      `${path}.download_url`,
      "null (a link is asked for with downloadPersonDataExport)",
      row["download_url"],
    );
  }
  const requestedAt = readText(row["requested_at"], `${path}.requested_at`);
  const when = () => context.formatWhen(requestedAt, now);
  return validatedDisplayView({
    personDataRequestId: readText(row["person_data_request_id"], `${path}.person_data_request_id`),
    kind: readKind(row["kind"], `${path}.kind`),
    get searchTermText() { return readNullableText(row["search_term_masked"], `${path}.search_term_masked`) ?? portalText("shell", "privacy.notOnRecord"); },
    get requestedByName() { return readNullableText(row["requested_by_name"], `${path}.requested_by_name`) ?? portalText("shell", "privacy.formerMember"); },
    get requestedText() { return queued && !handlerDeployed
        ? portalText("shell", "value_queued_nothing_processes_it_until_the_data_worker_is_791cf8a5", { v0: String(when()) })
        : when(); },
    progress: progressOf(row["progress"], `${path}.progress`),
    affectedCount: affectedCountOf(row["affected_count"], `${path}.affected_count`),
    exportFile: exportFileOf(row, path, now, context),
  });
}

/** The `Compliance` body as the screen renders it. */
export function complianceViewOf(body: unknown, operationId: string, context: SliceContext): ComplianceView {
  const data = readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
  const now = context.now();
  if (data["outbound_caller_id"] !== null) {
    throw new MalformedResponseError(
      `${operationId}.outbound_caller_id`,
      "null (branded caller identity is not modelled)",
      data["outbound_caller_id"],
    );
  }
  return {
    workspaceName: readText(data["workspace_name"], `${operationId}.workspace_name`),
    recording: recordingOf(data["recording"], `${operationId}.recording`),
    consentLine: readArray(data["consent_line"], `${operationId}.consent_line`).map((one, index) =>
      consentLineOf(one, `${operationId}.consent_line[${index}]`),
    ),
    retention: retentionOf(data["retention"], `${operationId}.retention`),
    operatorAccess: operatorAccessOf(data["operator_access"], now, context),
    outboundCallerId: null,
    suppressedCount: readInteger(data["suppressed_count"], `${operationId}.suppressed_count`),
    personDataRequest: readArray(data["person_data_request"], `${operationId}.person_data_request`).map(
      (one, index) =>
        personDataRequestRowOf(one, `${operationId}.person_data_request[${index}]`, now, context),
    ),
    personDataDeadlineHour: readInteger(data["person_data_deadline_hour"], `${operationId}.person_data_deadline_hour`),
  };
}

export function complianceApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "getCompliance"
  | "saveRetentionPolicy"
  | "previewRetentionPolicy"
  | "setOperatorAccess"
  | "requestPersonData"
  | "downloadPersonDataExport"
  | "saveConsentLine"
  | "restoreStandardConsentLine"
> {
  return {
    async getCompliance(): Promise<ComplianceView> {
      return complianceViewOf(assertOk(await client.getCompliance()), "getCompliance", context);
    },

    async saveRetentionPolicy(retentionClass: string, retainDay: number): Promise<ComplianceView> {
      return complianceViewOf(
        assertOk(
          await client.putRetentionPolicy({ path: { class: retentionClass }, body: { retain_day: retainDay } }),
        ),
        "putRetentionPolicy",
        context,
      );
    },

    async previewRetentionPolicy(retentionClass: string, retainDay: number): Promise<RetentionPreviewView> {
      const operationId = "previewRetentionPolicy";
      const data = readRecord(
        readRecord(
          assertOk(
            await client.previewRetentionPolicy({
              path: { class: retentionClass },
              query: { retain_day: retainDay },
            }),
          ),
          operationId,
        )["data"],
        `${operationId}.data`,
      );
      const returned = readText(data["class"], `${operationId}.class`);
      const known = RETENTION_CLASS.find((one) => one === returned);
      if (known === undefined) throw new MalformedResponseError(`${operationId}.class`, "a retention class", returned);
      return {
        retentionClass: known,
        retainDay: readInteger(data["retain_day"], `${operationId}.retain_day`),
        swept: readBoolean(data["swept"], `${operationId}.swept`),
        affectedCount: readInteger(data["affected_count"], `${operationId}.affected_count`),
      };
    },

    async setOperatorAccess(enabled: boolean): Promise<ComplianceView> {
      return complianceViewOf(
        assertOk(await client.putOperatorAccess({ body: { enabled } })),
        "putOperatorAccess",
        context,
      );
    },

    async requestPersonData(draft: PersonDataRequestDraftView): Promise<PersonDataRequestRowView> {
      const body = readRecord(
        assertOk(
          await client.createPersonDataRequest({ body: { kind: draft.kind, search_term: draft.searchTerm } }),
        ),
        "createPersonDataRequest",
      );
      return personDataRequestRowOf(body["data"], "createPersonDataRequest.data", context.now(), context);
    },

    async downloadPersonDataExport(personDataRequestId: string): Promise<PersonDataExportLinkView> {
      const operationId = "downloadPersonDataExport";
      const data = readRecord(
        readRecord(
          assertOk(
            await client.downloadPersonDataExport({ path: { person_data_request_id: personDataRequestId } }),
          ),
          operationId,
        )["data"],
        `${operationId}.data`,
      );
      return validatedDisplayView({
        downloadUrl: readText(data["download_url"], `${operationId}.download_url`),
        get expiresAtText() { return expiresInText(readText(data["expires_at"], `${operationId}.expires_at`), context.now()); },
      });
    },

    async saveConsentLine(languageCode: string, text: string): Promise<ConsentLineSavedView> {
      return consentLineSavedOf(
        assertOk(
          await client.putConsentLine({ path: { medium: "voice", language_code: languageCode }, body: { text } }),
        ),
        "putConsentLine",
        context,
      );
    },

    async restoreStandardConsentLine(languageCode: string): Promise<ConsentLineSavedView> {
      return consentLineSavedOf(
        assertOk(await client.deleteConsentLine({ path: { medium: "voice", language_code: languageCode } })),
        "deleteConsentLine",
        context,
      );
    },
  };
}
